package com.shreyasjoshi.components

import androidx.compose.runtime.*
import org.jetbrains.compose.web.dom.*
import com.shreyasjoshi.models.PortfolioData

@Composable
fun AboutSection(isDarkMode: Boolean) {
    val info = PortfolioData.personalInfo
    val education = PortfolioData.education
    val interests = PortfolioData.interests

    Section(
        attrs = {
            id("about")
            classNames(
                "portfolio-section",
                "py-16", "px-4", "sm:px-6", "lg:px-8", "max-w-6xl", "mx-auto", "border-b", "transition-colors",
                if (isDarkMode) "border-white/10" else "border-slate-200"
            )
        }
    ) {
        Div(attrs = { classNames("space-y-12") }) {
            // Section Header
            Div(attrs = { classNames("space-y-2") }) {
                Div(attrs = { classNames("text-xs", "font-mono", "text-[#189AD3]", "tracking-widest", "uppercase") }) {
                    Text("01 // PROFILE")
                }
                H2(
                    attrs = {
                        classNames(
                            "text-3xl", "font-bold", "tracking-tight",
                            if (isDarkMode) "text-white" else "text-slate-900"
                        )
                    }
                ) {
                    Text("Engineering Background & Core Specialization")
                }
            }

            // Paragraphs Grid
            Div(attrs = { classNames("grid", "grid-cols-1", "lg:grid-cols-3", "gap-6") }) {
                Div(attrs = { classNames("lg:col-span-2", "space-y-4") }) {
                    info.summaryParagraphs.forEach { para ->
                        P(
                            attrs = {
                                classNames(
                                    "text-sm", "sm:text-base", "leading-relaxed",
                                    if (isDarkMode) "text-white/70" else "text-slate-700"
                                )
                            }
                        ) {
                            Text(para)
                        }
                    }
                }

                // Core Philosophy Card
                Div(
                    attrs = {
                        classNames(
                            "p-5", "rounded-sm", "border", "space-y-4",
                            if (isDarkMode) "bg-[#0a0a0a] border-white/10" else "bg-slate-50 border-slate-200"
                        )
                    }
                ) {
                    Div(attrs = { classNames("text-xs", "font-mono", "font-semibold", "text-[#189AD3]", "uppercase") }) {
                        Text("Core Competency Highlights")
                    }
                    Ul(
                        attrs = {
                            classNames(
                                "space-y-2.5", "text-xs",
                                if (isDarkMode) "text-white/70" else "text-slate-600"
                            )
                        }
                    ) {
                        Li(attrs = { classNames("flex", "items-start", "gap-2") }) {
                            Span(attrs = { classNames("text-[#189AD3]", "font-mono") }) { Text("▸") }
                            Span { Text("Android Automotive OS & In-Vehicle Infotainment HMI") }
                        }
                        Li(attrs = { classNames("flex", "items-start", "gap-2") }) {
                            Span(attrs = { classNames("text-[#189AD3]", "font-mono") }) { Text("▸") }
                            Span { Text("AOSP CarSystemUI, CarLauncher & CarMessenger bug remediation") }
                        }
                        Li(attrs = { classNames("flex", "items-start", "gap-2") }) {
                            Span(attrs = { classNames("text-[#189AD3]", "font-mono") }) { Text("▸") }
                            Span { Text("Cuttlefish Virtualization & SDV Test Harness Automation") }
                        }
                        Li(attrs = { classNames("flex", "items-start", "gap-2") }) {
                            Span(attrs = { classNames("text-[#189AD3]", "font-mono") }) { Text("▸") }
                            Span { Text("SWE.1 / SWE.2 ASPICE Traceability in Codebeamer") }
                        }
                        Li(attrs = { classNames("flex", "items-start", "gap-2") }) {
                            Span(attrs = { classNames("text-[#189AD3]", "font-mono") }) { Text("▸") }
                            Span { Text("UML Modeling with PlantUML & MagicDraw") }
                        }
                    }
                }
            }

            // Education & Interests Subgrid
            Div(attrs = { classNames("grid", "grid-cols-1", "md:grid-cols-2", "gap-6", "pt-4") }) {
                // Education Card
                Div(
                    attrs = {
                        classNames(
                            "p-5", "rounded-sm", "border",
                            if (isDarkMode) "bg-[#0a0a0a] border-white/10" else "bg-slate-50 border-slate-200"
                        )
                    }
                ) {
                    Div(attrs = { classNames("text-xs", "font-mono", "text-[#189AD3]", "uppercase", "mb-2") }) {
                        Text("Formal Education")
                    }
                    education.forEach { edu ->
                        H3(
                            attrs = {
                                classNames(
                                    "text-base", "font-bold",
                                    if (isDarkMode) "text-white" else "text-slate-900"
                                )
                            }
                        ) {
                            Text(edu.degree)
                        }
                        Div(
                            attrs = {
                                classNames(
                                    "text-xs", "font-medium", "mt-1",
                                    if (isDarkMode) "text-white/60" else "text-slate-600"
                                )
                            }
                        ) {
                            Text("${edu.institution} • ${edu.location}")
                        }
                        Div(attrs = { classNames("text-xs", "font-mono", "text-[#189AD3]", "mt-1") }) {
                            Text("${edu.period} • ${edu.score}")
                        }
                        P(
                            attrs = {
                                classNames(
                                    "text-xs", "mt-2", "leading-relaxed",
                                    if (isDarkMode) "text-white/50" else "text-slate-500"
                                )
                            }
                        ) {
                            Text(edu.details)
                        }
                    }
                }

                // Interests & Pursuits Card
                Div(
                    attrs = {
                        classNames(
                            "p-5", "rounded-sm", "border",
                            if (isDarkMode) "bg-[#0a0a0a] border-white/10" else "bg-slate-50 border-slate-200"
                        )
                    }
                ) {
                    Div(attrs = { classNames("text-xs", "font-mono", "text-[#189AD3]", "uppercase", "mb-2") }) {
                        Text("Interests & Disciplines")
                    }
                    Div(attrs = { classNames("space-y-3") }) {
                        interests.forEach { intCat ->
                            Div {
                                Div(
                                    attrs = {
                                        classNames(
                                            "text-xs", "font-semibold",
                                            if (isDarkMode) "text-white/80" else "text-slate-800"
                                        )
                                    }
                                ) {
                                    Text(intCat.category)
                                }
                                Div(attrs = { classNames("flex", "flex-wrap", "gap-1.5", "mt-1.5") }) {
                                    intCat.items.forEach { item ->
                                        Span(
                                            attrs = {
                                                classNames(
                                                    "px-2", "py-0.5", "rounded-sm", "text-[11px]", "font-mono", "border",
                                                    if (isDarkMode) "bg-white/5 border-white/10 text-white/70"
                                                    else "bg-slate-200/50 border-slate-300 text-slate-700"
                                                )
                                            }
                                        ) {
                                            Text(item)
                                        }
                                    }
                                }
                            }
                        }
                    }
                }
            }
        }
    }
}


